/**
 * <gift-box-toggle>
 *
 * Adds / removes / swaps a gift box variant in the cart.
 *
 * Why a custom element:
 *   The cart drawer is refreshed by replacing innerHTML. Listeners bound on
 *   DOMContentLoaded are lost on every refresh (the previous implementation
 *   stopped working after the first drawer update). connectedCallback fires
 *   every time the element is inserted, so it re-binds itself automatically.
 *
 * All cart mutations go through a single POST /cart/update.js keyed by
 * variant ID. A variant swap is therefore one request ({old: 0, new: 1})
 * rather than a remove + add pair that can leave the cart half-updated.
 *
 * Expected markup (see snippets/cart-discount.liquid):
 *   <gift-box-toggle data-default-variant-id data-current-variant-id?>
 *     <input type="checkbox" data-gift-checkbox>
 *     <select data-gift-variant>   (optional — omitted for single variant)
 *     <p data-gift-error hidden>
 *   </gift-box-toggle>
 */
(() => {
  if (customElements.get('gift-box-toggle')) return;

  // Respects locale-prefixed routes under Shopify Markets (e.g. /en-ie/).
  const CART_UPDATE_URL = `${window.Shopify?.routes?.root ?? '/'}cart/update.js`;
  const FALLBACK_ERROR = 'Sorry, we couldn’t update your bag. Please try again.';

  class GiftBoxToggle extends HTMLElement {
    /**
     * Info panel open state lives on the class, not the instance, so it
     * survives the drawer re-render that follows every cart change
     * (otherwise the panel would snap shut as soon as a size is picked).
     */
    static infoOpen = false;

    connectedCallback() {
      this.checkbox = this.querySelector('[data-gift-checkbox]');
      this.select = this.querySelector('[data-gift-variant]');
      this.errorEl = this.querySelector('[data-gift-error]');
      this.infoToggle = this.querySelector('[data-gift-info-toggle]');
      this.infoPanel = this.querySelector('[data-gift-info-panel]');
      this.pending = false;

      if (!this.checkbox) return;

      // One AbortController removes every listener on disconnect.
      this.listeners = new AbortController();
      const { signal } = this.listeners;
      this.checkbox.addEventListener('change', () => this.onToggle(), { signal });
      this.select?.addEventListener('change', () => this.onVariantChange(), { signal });

      if (this.infoToggle && this.infoPanel) {
        // Restore previous state instantly (no animation) after a re-render.
        this.setInfoOpen(GiftBoxToggle.infoOpen, { animate: false });
        this.infoToggle.addEventListener(
          'click',
          () => this.setInfoOpen(!GiftBoxToggle.infoOpen),
          { signal },
        );
      }
    }

    setInfoOpen(open, { animate = true } = {}) {
      GiftBoxToggle.infoOpen = open;

      if (!animate) this.infoPanel.classList.add('is-instant');

      this.infoToggle.setAttribute('aria-expanded', String(open));
      this.infoPanel.classList.toggle('is-open', open);

      if (!animate) {
        // Re-enable transitions after the instant state has been painted.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.infoPanel?.classList.remove('is-instant'));
        });
      }
    }

    disconnectedCallback() {
      this.listeners?.abort();
    }

    /** Variant ID currently in the cart (server-rendered, then kept in sync). */
    get currentVariantId() {
      return this.dataset.currentVariantId || null;
    }

    set currentVariantId(id) {
      if (id) this.dataset.currentVariantId = String(id);
      else delete this.dataset.currentVariantId;
    }

    /** Variant the customer has chosen (falls back to the only eligible variant). */
    get selectedVariantId() {
      return this.select?.value || this.dataset.defaultVariantId;
    }

    onToggle() {
      if (this.checkbox.checked) {
        const id = this.selectedVariantId;
        if (!id) return;
        this.commit({ [id]: 1 }, id, () => { this.checkbox.checked = false; });
        return;
      }

      const id = this.currentVariantId;
      if (!id) return;
      this.commit({ [id]: 0 }, null, () => { this.checkbox.checked = true; });
    }

    onVariantChange() {
      const prev = this.currentVariantId;
      const next = this.select.value;

      // Unchecked: the select is just a pre-selection, nothing to sync.
      if (!this.checkbox.checked || !prev || prev === next) return;

      this.commit({ [prev]: 0, [next]: 1 }, next, () => { this.select.value = prev; });
    }

    /**
     * @param {Record<string, number>} updates  variantId -> quantity
     * @param {string|null} nextVariantId       variant in cart after success
     * @param {() => void} revert               restores UI on failure
     */
    async commit(updates, nextVariantId, revert) {
      if (this.pending) {
        revert();
        return;
      }

      this.setPending(true);
      this.showError('');

      try {
        const response = await fetch(CART_UPDATE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          // 422s (sold out, inventory limits) include a customer-safe description.
          throw new Error(data.description || data.message || FALLBACK_ERROR);
        }

        this.currentVariantId = nextVariantId;

        this.dispatchEvent(new CustomEvent('gift-box:change', {
          bubbles: true,
          detail: { cart: data, variantId: nextVariantId },
        }));

        this.refreshCart();
      } catch (error) {
        console.error('[gift-box-toggle]', error);
        revert();
        this.showError(error.message || FALLBACK_ERROR);
      } finally {
        this.setPending(false);
      }
    }

    refreshCart() {
      // Provided by the cart-discount snippet. Swap for the theme's native
      // cart refresh event if one exists — it is cheaper than a full page fetch.
      if (typeof window.cartDrawerUpdate === 'function') {
        window.cartDrawerUpdate();
      } else {
        window.location.reload();
      }
    }

    setPending(isPending) {
      this.pending = isPending;
      this.toggleAttribute('aria-busy', isPending);
      this.checkbox.disabled = isPending;
      if (this.select) this.select.disabled = isPending;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }
  }

  customElements.define('gift-box-toggle', GiftBoxToggle);
})();